/**
 * Consistent AI model colors by family across all charts/pages.
 * Claude = red-orange, GPT = blue, Composer = green, Auto/Default = purple, Other = grey.
 */

const OTHER_AGGREGATE_COLOR = '#9CA3AF';

const FAMILY_PALETTES = {
  claude: ['#B91C1C', '#DC2626', '#EA580C', '#F97316', '#FB923C'],
  gpt: ['#1E3A8A', '#1D4ED8', '#2563EB', '#3B82F6', '#60A5FA'],
  composer: ['#14532D', '#166534', '#16A34A', '#22C55E', '#4ADE80'],
  auto: ['#7C3AED'],
  other: ['#6B7280', '#9CA3AF', '#78716C', '#A8A29E']
};

function hashString(str) {
  let h = 0;
  for (let i = 0; i < str.length; i += 1) {
    h = ((h << 5) - h) + str.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

export function normalizeModelName(name) {
  return (name || 'Unknown').trim().toLowerCase();
}

function stripProviderPrefix(name) {
  return name
    .replace(/^anthropic\./, '')
    .replace(/^openai\./, '');
}

/** Claude effort/thinking suffixes stripped for chart grouping (longest first). */
const CLAUDE_VARIANT_SUFFIXES = [
  'thinking-xhigh',
  'thinking-xlow',
  'thinking-high',
  'thinking-medium',
  'thinking-med',
  'thinking-low',
  'thinking',
  'xhigh',
  'xlow',
  'high',
  'medium',
  'med',
  'low'
];

/**
 * Group Claude sub-variants (high/medium/low/thinking/xhigh, etc.) under one model key.
 * Non-Claude models are only lowercased/trimmed.
 */
export function consolidateModelNameForGrouping(name) {
  const raw = normalizeModelName(name);
  const n = stripProviderPrefix(raw);
  if (!n.includes('claude') && !n.includes('anthropic')) {
    return raw;
  }

  let base = n;
  let changed = true;
  while (changed) {
    changed = false;
    for (const suffix of CLAUDE_VARIANT_SUFFIXES) {
      const re = new RegExp(`-${suffix.replace(/-/g, '\\-')}$`);
      if (re.test(base)) {
        base = base.replace(re, '');
        changed = true;
        break;
      }
    }
  }
  return base;
}

/**
 * Merge rows that share the same consolidated model name (sums count/events/tokens/cents).
 */
export function aggregateModelRecords(records, options = {}) {
  const { modelKey = 'model' } = options;
  const map = new Map();

  for (const row of records || []) {
    const rawModel = row?.[modelKey] || 'Unknown';
    const key = consolidateModelNameForGrouping(rawModel);
    const count = Number(row?.count ?? row?.events ?? 0) || 0;
    const existing = map.get(key) || {
      model: key,
      count: 0,
      events: 0,
      tokens: 0,
      cents: 0
    };
    existing.count += count;
    existing.events += count;
    existing.tokens += Number(row?.tokens ?? 0) || 0;
    existing.cents += Number(row?.cents ?? 0) || 0;
    map.set(key, existing);
  }

  return Array.from(map.values()).sort((a, b) => (b.count || b.events) - (a.count || a.events));
}

/**
 * @returns {'claude' | 'gpt' | 'composer' | 'auto' | 'other'}
 */
export function getModelFamily(name) {
  const raw = normalizeModelName(name);
  if (!raw || raw === 'unknown') return 'other';
  if (raw === 'other') return 'other';

  const n = stripProviderPrefix(raw);

  if (n === 'default' || n === 'auto' || n.startsWith('auto-') || n.includes('default')) {
    return 'auto';
  }
  if (n.includes('composer')) {
    return 'composer';
  }
  if (n.includes('claude') || n.includes('anthropic')) {
    return 'claude';
  }
  if (n.includes('gpt') || n.includes('openai')) {
    return 'gpt';
  }

  return 'other';
}

/**
 * Stable hex color for a model label. Same model name → same color on every page/chart.
 * @param {string} name - Display model name
 * @param {number} [fallbackIndex=0] - Used only when name is empty
 */
export function getModelColor(name, fallbackIndex = 0) {
  const n = normalizeModelName(name);

  if (!name || !String(name).trim()) {
    return FAMILY_PALETTES.other[fallbackIndex % FAMILY_PALETTES.other.length];
  }

  if (n === 'other') {
    return OTHER_AGGREGATE_COLOR;
  }

  const family = getModelFamily(name);
  const palette = FAMILY_PALETTES[family] || FAMILY_PALETTES.other;
  const key = consolidateModelNameForGrouping(name);
  return palette[hashString(stripProviderPrefix(key)) % palette.length];
}
